import React, { useState, useEffect, useCallback } from 'react';
import { Card, List, Tag, Button, Space, Typography, Empty, Popconfirm, message } from 'antd';
import { CheckOutlined, DeleteOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import api from '../api';
import { useI18n } from '../i18n';

const LEVEL_TAG = {
  info: { color: 'blue', key: '信息' },
  warning: { color: 'orange', key: '警告' },
  error: { color: 'red', key: '异常' }
};

// 站内消息页面：自动交易等后台任务的异常与关键事件；onBack 返回主界面
const Notifications = ({ onUnreadChange, onBack }) => {
  const { t, ts } = useI18n();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      setList(res.data.data.list);
      onUnreadChange?.(res.data.data.unread);
    } catch (err) {
      message.error(t('加载消息失败: ') + ts(err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [onUnreadChange, t, ts]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReadAll = async () => {
    try {
      await api.post('/notifications/read-all');
      load();
    } catch (err) {
      message.error(t('操作失败: ') + ts(err.response?.data?.error || err.message));
    }
  };

  const handleClear = async () => {
    try {
      await api.delete('/notifications');
      message.success(t('已清空'));
      load();
    } catch (err) {
      message.error(t('清空失败: ') + ts(err.response?.data?.error || err.message));
    }
  };

  return (
    <Card
      title={
        <Space>
          {onBack && <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} />}
          {t('站内消息')}
        </Space>
      }
      extra={
        <Space>
          <Button icon={<CheckOutlined />} onClick={handleReadAll} disabled={!list.some((n) => !n.isRead)}>
            {t('全部已读')}
          </Button>
          <Popconfirm title={t('清空全部消息？')} okText={t('清空')} cancelText={t('取消')} onConfirm={handleClear}>
            <Button danger icon={<DeleteOutlined />} disabled={!list.length}>
              {t('清空')}
            </Button>
          </Popconfirm>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            {t('刷新')}
          </Button>
        </Space>
      }
    >
      <List
        loading={loading}
        dataSource={list}
        locale={{ emptyText: <Empty description={t('暂无消息')} /> }}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        renderItem={(n) => {
          const level = LEVEL_TAG[n.level] || LEVEL_TAG.info;
          return (
            <List.Item key={n.notificationId} style={{ opacity: n.isRead ? 0.55 : 1 }}>
              <List.Item.Meta
                title={
                  <Space>
                    <Tag color={level.color}>{t(level.key)}</Tag>
                    <Typography.Text strong={!n.isRead}>{ts(n.title)}</Typography.Text>
                    {!n.isRead && <Tag color="processing">{t('未读')}</Tag>}
                  </Space>
                }
                description={
                  <Space direction="vertical" size={2}>
                    {n.content && <Typography.Text type="secondary">{ts(n.content)}</Typography.Text>}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {new Date(n.createdAt).toLocaleString()} · {t('来源：')}
                      {n.source === 'auto_trade' ? t('自动交易_src') : n.source === 'price_alert' ? t('价格提醒_src') : n.source}
                    </Typography.Text>
                  </Space>
                }
              />
            </List.Item>
          );
        }}
      />
    </Card>
  );
};

export default Notifications;
